import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../auth/api';
import SkyDecor from '../../components/SkyDecor';
import './PracticeBuddyPage.css';

// ── Constants ────────────────────────────────────────────────────────────────
const MAX_RECORDING_MS = 30000; // 30 seconds max recording
const COOLDOWN_MS = 3000;       // 3 second cooldown between interactions
const INACTIVITY_MS = 10 * 60 * 1000; // 10 minute inactivity timeout

const STATES = {
  IDLE: 'idle',
  LISTENING: 'listening',
  PROCESSING: 'processing',
  SPEAKING: 'speaking',
  ERROR: 'error',
};

const CAPTIONS = {
  [STATES.IDLE]: 'Tap the mic and talk to me! 🎤',
  [STATES.LISTENING]: "I'm listening... 👂",
  [STATES.PROCESSING]: 'Let me think... 🤔',
  [STATES.SPEAKING]: '',
  [STATES.ERROR]: "Oops! Buddy couldn't hear you. Try again! 😅",
};

const EMOJIS = {
  [STATES.IDLE]: '🤖',
  [STATES.LISTENING]: '👂',
  [STATES.PROCESSING]: '🤔',
  [STATES.SPEAKING]: '🗣️',
  [STATES.ERROR]: '😵',
};


const PracticeBuddyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { childId, childName } = location.state || {};

  // ── State ──────────────────────────────────────────────────────────────
  const [buddyState, setBuddyState] = useState(STATES.IDLE);
  const [sessionId, setSessionId] = useState(null);
  const [caption, setCaption] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [cooldown, setCooldown] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const recordTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // ── Inactivity Timeout ─────────────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(async () => {
      // Auto-end session and navigate back
      if (sessionId) {
        try { await api.post(`/buddy/end-session/${sessionId}`); } catch {}
      }
      navigate('/child/hub', { state: { childId, childName } });
    }, INACTIVITY_MS);
  }, [sessionId, navigate, childId, childName]);

  // ── Start Session on Mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!childId) {
      navigate('/child/hub');
      return;
    }

    const startSession = async () => {
      try {
        const res = await api.post('/buddy/start-session', { child_id: childId });
        setSessionId(res.data.id);
        setCaption(`Hi ${childName || 'friend'}! I'm your Practice Buddy! ${CAPTIONS[STATES.IDLE]}`);
      } catch (err) {
        console.error('Failed to start session:', err);
        setCaption('Oops! Could not start a session. Go back and try again.');
        setBuddyState(STATES.ERROR);
      }
    };

    startSession();
    resetInactivityTimer();

    return () => {
      // Cleanup on unmount
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // ── End Session Helper ─────────────────────────────────────────────────
  const handleBack = async () => {
    if (sessionId) {
      try { await api.post(`/buddy/end-session/${sessionId}`); } catch {}
    }
    navigate('/child/hub', { state: { childId, childName } });
  };

  // ── Start Recording ────────────────────────────────────────────────────
  const startRecording = async () => {
    if (buddyState !== STATES.IDLE || cooldown || !sessionId) return;

    resetInactivityTimer();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Determine supported mime type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // Stop all tracks
        stream.getTracks().forEach(t => t.stop());
        if (recordTimerRef.current) clearTimeout(recordTimerRef.current);

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        sendAudioToBackend(audioBlob);
      };

      recorder.start();
      setBuddyState(STATES.LISTENING);
      setCaption(CAPTIONS[STATES.LISTENING]);

      // Auto-stop after MAX_RECORDING_MS
      recordTimerRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_RECORDING_MS);

    } catch (err) {
      console.error('Microphone access denied:', err);
      setBuddyState(STATES.ERROR);
      setCaption('Please allow microphone access to talk with Buddy! 🎤');
    }
  };

  // ── Stop Recording ─────────────────────────────────────────────────────
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // ── Send Audio to Backend ──────────────────────────────────────────────
  const sendAudioToBackend = async (audioBlob) => {
    setBuddyState(STATES.PROCESSING);
    setCaption(CAPTIONS[STATES.PROCESSING]);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const res = await api.post(`/buddy/interact/${sessionId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = res.data;

      if (data.status === 'error') {
        setBuddyState(STATES.ERROR);
        setCaption(data.error_message || CAPTIONS[STATES.ERROR]);
        startCooldown();
        return;
      }

      // Add messages to transcript
      if (data.message_text) {
        setTranscript(prev => [
          ...prev.slice(-8), // Keep last 8 messages for display
          { role: 'assistant', text: data.message_text },
        ]);
      }

      // Play the audio response
      if (data.audio_url) {
        setBuddyState(STATES.SPEAKING);
        setCaption(data.message_text || 'Buddy is talking!');

        const audio = new Audio(data.audio_url);
        audioPlayerRef.current = audio;

        audio.onended = () => {
          setBuddyState(STATES.IDLE);
          setCaption(CAPTIONS[STATES.IDLE]);
          startCooldown();
        };

        audio.onerror = () => {
          setBuddyState(STATES.IDLE);
          setCaption(CAPTIONS[STATES.IDLE]);
          startCooldown();
        };

        audio.play().catch(() => {
          // Autoplay might be blocked
          setBuddyState(STATES.IDLE);
          setCaption(data.message_text || CAPTIONS[STATES.IDLE]);
          startCooldown();
        });
      } else {
        // No audio URL, just show text
        setBuddyState(STATES.IDLE);
        setCaption(data.message_text || CAPTIONS[STATES.IDLE]);
        startCooldown();
      }

    } catch (err) {
      console.error('Interaction failed:', err);
      setBuddyState(STATES.ERROR);
      setCaption(CAPTIONS[STATES.ERROR]);
      startCooldown();
    }
  };

  // ── Cooldown ───────────────────────────────────────────────────────────
  const startCooldown = () => {
    setCooldown(true);
    cooldownTimerRef.current = setTimeout(() => {
      setCooldown(false);
    }, COOLDOWN_MS);
  };

  // ── Mic Button Click Handler ───────────────────────────────────────────
  const handleMicClick = () => {
    if (buddyState === STATES.LISTENING) {
      stopRecording();
    } else if (buddyState === STATES.IDLE && !cooldown) {
      startRecording();
    } else if (buddyState === STATES.ERROR) {
      setBuddyState(STATES.IDLE);
      setCaption(CAPTIONS[STATES.IDLE]);
    }
  };

  // ── Mic Button Style ──────────────────────────────────────────────────
  const getMicClass = () => {
    if (buddyState === STATES.LISTENING) return 'mic-recording';
    if (buddyState === STATES.PROCESSING || buddyState === STATES.SPEAKING) return 'mic-disabled';
    if (cooldown) return 'mic-disabled';
    return 'mic-idle';
  };

  const getMicEmoji = () => {
    if (buddyState === STATES.LISTENING) return '⏹️';
    if (buddyState === STATES.PROCESSING || buddyState === STATES.SPEAKING) return '⏳';
    return '🎤';
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="buddy-container sb-page">
      <SkyDecor />
      {/* Header */}
      <div className="buddy-header">
        <button className="buddy-back-btn sb-icon-btn" onClick={handleBack} title="Back">
          ←
        </button>
        <div className="buddy-title">
          🤖 Practice Buddy
        </div>
      </div>

      {/* Main Content */}
      <div className="buddy-content">
        {/* Animated Character */}
        <div className={`buddy-character state-${buddyState}`}>
          {EMOJIS[buddyState]}
        </div>

        {/* Status Caption */}
        <div className="buddy-caption">
          {caption}
        </div>

        {/* Recent Transcript */}
        {transcript.length > 0 && (
          <div className="buddy-transcript">
            {transcript.slice(-3).map((msg, i) => (
              <div key={i} className={`buddy-transcript-msg role-${msg.role}`}>
                <span className="buddy-transcript-label">
                  {msg.role === 'user' ? 'You:' : 'Buddy:'}
                </span>
                {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Microphone Button */}
        <div className="buddy-mic-area">
          <button
            className={`buddy-mic-btn ${getMicClass()}`}
            onClick={handleMicClick}
            disabled={buddyState === STATES.PROCESSING || buddyState === STATES.SPEAKING}
          >
            {getMicEmoji()}
          </button>
          {cooldown && (
            <div className="buddy-cooldown">Wait a moment...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeBuddyPage;
